import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils";
/**
 * Backdrop refraction matching Figma's native Glass effect
 * (refraction 0.8, depth 100, dispersion 0.5; frost radius per surface).
 *
 * A canvas-generated displacement map encodes the rounded-rect "lens": the
 * outward surface normal scaled by an ease profile across the edge band. An
 * SVG filter displaces the backdrop through it — split per RGB channel at
 * slightly different strengths for true chromatic dispersion — via
 * `backdrop-filter: url(#…)`, which only Chromium composes. Other engines fall
 * back to the frosted surface the caller supplies, so `data-refracting` marks
 * when the live lens is active and the fallback edge treatment should drop.
 */
/** Default Figma cornerRadius on glass surfaces. */
const CORNER_RADIUS = 30;
/** px lens-edge width at Figma depth 100. */
const EDGE_BAND = 56;
/** px peak refraction offset at Figma refraction 80. */
const MAX_SHIFT = 36;
/** Per-channel scale delta at Figma dispersion 50. */
const DISPERSION = 0.14;
function sdfRoundedRect(x, y, width, height, radius) {
    const qx = Math.abs(x - width / 2) - (width / 2 - radius);
    const qy = Math.abs(y - height / 2) - (height / 2 - radius);
    const ax = Math.max(qx, 0);
    const ay = Math.max(qy, 0);
    return Math.min(Math.max(qx, qy), 0) + Math.hypot(ax, ay) - radius;
}
function makeDisplacementMap(width, height, radius, edgeBand) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context)
        return null;
    const image = context.createImageData(width, height);
    const data = image.data;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const distance = sdfRoundedRect(x + 0.5, y + 0.5, width, height, radius);
            let px = 0.5;
            let py = 0.5;
            if (distance > -edgeBand && distance <= 0) {
                // 0 at the band's inner limit, 1 at the edge.
                const t = 1 + distance / edgeBand;
                const profile = t * t * (3 - 2 * t); // smoothstep
                let nx = sdfRoundedRect(x + 1.5, y + 0.5, width, height, radius) -
                    sdfRoundedRect(x - 0.5, y + 0.5, width, height, radius);
                let ny = sdfRoundedRect(x + 0.5, y + 1.5, width, height, radius) -
                    sdfRoundedRect(x + 0.5, y - 0.5, width, height, radius);
                const length = Math.hypot(nx, ny) || 1;
                nx /= length;
                ny /= length;
                px = 0.5 + 0.5 * nx * profile;
                py = 0.5 + 0.5 * ny * profile;
            }
            const i = (y * width + x) * 4;
            data[i] = Math.round(px * 255);
            data[i + 1] = Math.round(py * 255);
            data[i + 2] = 128;
            data[i + 3] = 255;
        }
    }
    context.putImageData(image, 0, 0);
    return canvas.toDataURL();
}
const isChromium = () => typeof navigator !== "undefined" &&
    /Chrome|Chromium|Edg\//.test(navigator.userAgent);
/**
 * Generic refracting surface. Sets `data-refracting` while the displacement
 * filter is active so styles can drop their fallback edge treatments with a
 * `data-refracting:` variant.
 */
export const Refract = React.forwardRef(({ children, className, style: surfaceStyle, refract = true, frost = 4, radius = CORNER_RADIUS, refraction = 80, depth = 100, dispersion = 50, as: Component = "div", elementRef, ...props }, forwardedRef) => {
    const ref = React.useRef(null);
    const filterId = `glass-${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;
    const [lens, setLens] = React.useState(null);
    React.useLayoutEffect(() => {
        if (!isChromium() || !refract) {
            setLens(null);
            return;
        }
        const element = ref.current;
        if (!element)
            return;
        let last = { width: 0, height: 0 };
        const update = () => {
            const width = Math.round(element.offsetWidth);
            const height = Math.round(element.offsetHeight);
            if (!width || !height)
                return;
            if (width === last.width && height === last.height)
                return;
            last = { width, height };
            const map = makeDisplacementMap(width, height, radius, Math.max(1, EDGE_BAND * (depth / 100)));
            if (map)
                setLens({ map, width, height });
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [depth, radius, refract]);
    // feDisplacementMap offsets by scale * (channel - 0.5).
    const scale = MAX_SHIFT * 2 * (refraction / 80);
    const channelDispersion = DISPERSION * (dispersion / 50);
    const style = { ...surfaceStyle, borderRadius: radius };
    if (lens) {
        const filter = `url(#${filterId}) blur(${frost}px) saturate(1.2)`;
        style.backdropFilter = filter;
        style.WebkitBackdropFilter = filter;
    }
    const setRef = (element) => {
        ref.current = element;
        elementRef?.(element);
        if (typeof forwardedRef === "function") {
            forwardedRef(element);
        }
        else if (forwardedRef) {
            forwardedRef.current = element;
        }
    };
    return (_jsxs(Component, { ref: setRef, className: cn(className), "data-refracting": lens ? "" : undefined, style: style, ...props, children: [lens && (_jsx("svg", { "aria-hidden": "true", className: "absolute", height: "0", width: "0", children: _jsxs("filter", { colorInterpolationFilters: "sRGB", filterUnits: "userSpaceOnUse", height: lens.height, id: filterId, width: lens.width, x: "0", y: "0", children: [_jsx("feImage", { height: lens.height, href: lens.map, result: "map", width: lens.width, x: "0", y: "0" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "map", result: "dispR", scale: scale * (1 + channelDispersion), xChannelSelector: "R", yChannelSelector: "G" }), _jsx("feColorMatrix", { in: "dispR", result: "red", type: "matrix", values: "1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "map", result: "dispG", scale: scale, xChannelSelector: "R", yChannelSelector: "G" }), _jsx("feColorMatrix", { in: "dispG", result: "green", type: "matrix", values: "0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "map", result: "dispB", scale: scale * (1 - channelDispersion), xChannelSelector: "R", yChannelSelector: "G" }), _jsx("feColorMatrix", { in: "dispB", result: "blue", type: "matrix", values: "0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" }), _jsx("feBlend", { in: "red", in2: "green", mode: "screen", result: "rg" }), _jsx("feBlend", { in: "rg", in2: "blue", mode: "screen" })] }) })), children] }));
});
Refract.displayName = "Refract";
//# sourceMappingURL=refract.js.map